/**
 * PlanCard - Pricing plan display card
 *
 * Features:
 * - Monthly/annual pricing display
 * - Feature highlights with icons
 * - Subscriber count and MRR stats
 * - Quick action buttons
 * - Hover animations
 */
import { cn } from '@respectly/ui';
import { Card, CardContent, CardHeader, Button, Skeleton } from '@respectly/ui';
import {
  Check,
  Users,
  DollarSign,
  MessageSquare,
  Mail,
  Pencil,
  Eye,
  Sparkles,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { PlanStatusBadge } from './PlanStatusBadge';
import type { PlanWithStats, PlanStatus } from '@respectly/shared-types';
import { buildPlanFeatures } from '@respectly/utils';

// ============================================
// Types
// ============================================

interface PlanCardProps {
  plan: PlanWithStats;
  onEdit?: () => void;
  onViewSubscribers?: () => void;
  className?: string;
}

interface PlanCardGridProps {
  plans: PlanWithStats[];
  onEdit?: (planId: string) => void;
  onViewSubscribers?: (planId: string) => void;
  className?: string;
}

// ============================================
// Formatters
// ============================================

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const compactCurrencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

function formatCurrency(value: number): string {
  return currencyFormatter.format(value);
}

function formatCompactCurrency(value: number): string {
  if (value >= 1000) {
    return compactCurrencyFormatter.format(value);
  }
  return currencyFormatter.format(value);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

// ============================================
// Component
// ============================================

export function PlanCard({
  plan,
  onEdit,
  onViewSubscribers,
  className,
}: PlanCardProps) {
  const features = buildPlanFeatures(plan).slice(0, 5);
  const planStatus: PlanStatus = plan.isActive ? 'active' : 'inactive';
  const annualSavings = plan.monthlyPrice * 12 - plan.annualPrice;
  const savingsPercent = plan.monthlyPrice > 0
    ? Math.round((annualSavings / (plan.monthlyPrice * 12)) * 100)
    : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.3 }}
      className={className}
    >
      <Card className="relative h-full overflow-hidden border-slate-200/60 dark:border-slate-700/60 bg-white dark:bg-slate-900 hover:shadow-lg transition-shadow duration-300">
        {/* Gradient accent */}
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" />

        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-lg font-bold text-slate-900 dark:text-white truncate">
                  {plan.name}
                </h3>
                <PlanStatusBadge status={planStatus} size="sm" />
              </div>
              {plan.description && (
                <p className="text-sm text-slate-500 dark:text-slate-400 line-clamp-2">
                  {plan.description}
                </p>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Pricing */}
          <div className="space-y-2">
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-bold text-slate-900 dark:text-white tabular-nums">
                {formatCurrency(plan.monthlyPrice)}
              </span>
              <span className="text-sm text-slate-500">/month</span>
            </div>
            {plan.annualPrice > 0 && savingsPercent > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">
                  {formatCurrency(plan.annualPrice)}/year
                </span>
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                  Save {savingsPercent}%
                </span>
              </div>
            )}
          </div>

          {/* Limits */}
          <div className="grid grid-cols-2 gap-2 py-3 border-y border-slate-100 dark:border-slate-800">
            <div className="text-center border-r border-slate-100 dark:border-slate-800">
              <div className="flex items-center justify-center gap-1 text-slate-500 mb-0.5">
                <MessageSquare className="h-3.5 w-3.5" />
              </div>
              <p className="text-sm font-semibold text-slate-900 dark:text-white tabular-nums">
                {formatNumber(plan.smsCreditsPerMonth)}
              </p>
              <p className="text-[10px] text-slate-500">SMS/mo</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 text-slate-500 mb-0.5">
                <Mail className="h-3.5 w-3.5" />
              </div>
              <p className="text-sm font-semibold text-slate-900 dark:text-white tabular-nums">
                {formatNumber(plan.emailCreditsPerMonth)}
              </p>
              <p className="text-[10px] text-slate-500">emails/mo</p>
            </div>
          </div>

          {/* Features */}
          {features.length > 0 && (
            <div className="space-y-1.5">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start gap-2">
                  <div className="flex-shrink-0 h-4 w-4 mt-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                    <Check className="h-2.5 w-2.5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">
                      {feature.label}
                    </p>
                    {feature.value && (
                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                        {feature.value}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Stats */}
          <div className="flex items-center justify-between pt-3 border-t border-slate-100 dark:border-slate-800">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5">
                <Users className="h-4 w-4 text-slate-400" />
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300 tabular-nums">
                  {plan.subscriberCount}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <DollarSign className="h-4 w-4 text-slate-400" />
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300 tabular-nums">
                  {formatCompactCurrency(plan.mrrContribution)}
                </span>
                <span className="text-xs text-slate-400">MRR</span>
              </div>
            </div>
            <span className="text-xs text-slate-400 tabular-nums">
              {plan.percentOfTotal.toFixed(1)}% of total
            </span>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={onViewSubscribers}
            >
              <Eye className="h-3.5 w-3.5 mr-1.5" />
              Subscribers
            </Button>
            <Button
              variant="default"
              size="sm"
              className="flex-1"
              onClick={onEdit}
            >
              <Pencil className="h-3.5 w-3.5 mr-1.5" />
              Edit
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

// ============================================
// Grid Component
// ============================================

export function PlanCardGrid({
  plans,
  onEdit,
  onViewSubscribers,
  className,
}: PlanCardGridProps) {
  if (plans.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="h-16 w-16 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
          <Sparkles className="h-8 w-8 text-slate-400" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">
          No plans yet
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm">
          Create your first pricing plan to start offering subscriptions to your customers.
        </p>
      </div>
    );
  }

  return (
    <div className={cn('grid gap-6 sm:grid-cols-2 lg:grid-cols-3', className)}>
      {plans.map((plan, index) => (
        <motion.div
          key={plan.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: index * 0.1 }}
        >
          <PlanCard
            plan={plan}
            onEdit={() => onEdit?.(plan.id)}
            onViewSubscribers={() => onViewSubscribers?.(plan.id)}
          />
        </motion.div>
      ))}
    </div>
  );
}

// ============================================
// Skeleton
// ============================================

export function PlanCardSkeleton({ className }: { className?: string }) {
  return (
    <Card className={cn('border-slate-200/60 dark:border-slate-700/60', className)}>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2 mb-2">
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-5 w-16" />
        </div>
        <Skeleton className="h-4 w-full" />
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Skeleton className="h-8 w-20 mb-1" />
          <Skeleton className="h-4 w-28" />
        </div>
        <div className="grid grid-cols-3 gap-2 py-3">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-2/3" />
        </div>
        <div className="flex gap-2 pt-3">
          <Skeleton className="h-9 flex-1" />
          <Skeleton className="h-9 flex-1" />
        </div>
      </CardContent>
    </Card>
  );
}

export function PlanCardGridSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: count }).map((_, i) => (
        <PlanCardSkeleton key={i} />
      ))}
    </div>
  );
}
